import React from "react";
import { Stack, Heading, Text } from "@chakra-ui/layout";
import { useColorModeValue } from "@chakra-ui/color-mode";
import {
  InputGroup,
  Box,
  Input,
  InputRightAddon,
  Flex,
  UnorderedList,
  ListItem,
  Link, Icon,
} from "@chakra-ui/react";
import { firebaseClient, getToken } from "firebaseClient";
import { getAccount, updateUser, getAuthUrl } from "../../utils/sessionHelper";
import { CustomSection } from "components/custom-section";
import { useSession } from "next-auth/react";
import { useFirebaseUser } from "../../utils/useFirebaseUser";
import { Partner } from "./partner";
import { v4 as uuidv4 } from 'uuid';
import { CopyToClipboard } from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { CopyIcon } from '@chakra-ui/icons';
import { textStyle } from "theme/names";
import { LemListIcon } from "components/page/icons/LemListIcon";
import lemlist from "public/logo/lemlist.png";



export function Integrations({ }) {

  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"

  const [refresh, refresher] = React.useState({});
  const isAuthenticated = useFirebaseUser();

  const generateKey = async () => {
    // console.log("generateKey");
    let key = uuidv4();
    await updateUser(session, { apiKey: key });
    refresher({});
  }

  React.useEffect(() => {
    // console.log("key: " + getAccount(session)?.apiKey);
    if (isAuthenticated && !getAccount(session)?.apiKey) {
      generateKey();
    }
  }, [isAuthenticated, refresh]);

  return (
    <Box>
      <Stack spacing={4}>
        <CustomSection
          title="API Key"
          description="Use your custom API Key to connect Taplio with other services"
          css={{ maxW: "600px" }}
        >
          <Stack padding={4}>
            <InputGroup size="md">
              <Input
                isDisabled={true}
                value={getAccount(session)?.apiKey ?? ""}
                borderRadius="10px 0px 0px 10px"
              />
              <CopyToClipboard
                text={getAccount(session)?.apiKey ?? ""}
                onCopy={() => {
                  toast.success("Post copied to clipboard");
                }}
              >
                <InputRightAddon
                  cursor="pointer"
                  children={
                    <Icon viewBox="0 0 17 17">
                      {" "}
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 17 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.6875 2.71875L14.7812 0.8125C14.5938 0.625 14.3438 0.5 14.0625 0.5H8.96875C7.84375 0.5 6.96875 1.40625 6.96875 2.5V10.5C7 11.625 7.875 12.5 9 12.5H15C16.0938 12.5 17 11.625 17 10.5V3.4375C17 3.15625 16.875 2.90625 16.6875 2.71875ZM15.5 10.5C15.5 10.7812 15.25 11 15 11H8.96875C8.6875 11 8.46875 10.7812 8.46875 10.5V2.53125C8.46875 2.25 8.6875 2.03125 8.96875 2.03125H12.9688L13 3.5C13 4.0625 13.4375 4.5 14 4.5H15.4688V10.5H15.5ZM9.5 14.5C9.5 14.7812 9.25 15 9 15H2.96875C2.6875 15 2.46875 14.7812 2.46875 14.5V6.53125C2.46875 6.25 2.71875 6.03125 2.96875 6.03125H6V4.5H2.96875C1.875 4.5 0.96875 5.40625 0.96875 6.5L1 14.5C1 15.625 1.875 16.5 3 16.5H9C10.0938 16.5 11 15.625 11 14.5V13.5H9.5V14.5Z"
                          fill="#0A66C2"
                        />
                      </svg>
                    </Icon>
                  }
                />
              </CopyToClipboard>
            </InputGroup>
          </Stack>
        </CustomSection>
        <CustomSection
          title="Partners"
          description="Discover amazing products we work with"
          css={{ maxW: "1000px" }}
        >
          <Stack padding={4} spacing={10}>
            {[
              {
                name: "Tweet Hunter",
                id: "keyTh",
                hasInputKey: true,
                desc: (
                  <Box>
                    <Text textStyle={textStyle["body.medium.light"]}>
                      Connect a Tweet Hunter account to post on Twitter using
                      Taplio
                    </Text>
                    {getAccount(session)?.keyTh ? (
                      <Text
                        textStyle={textStyle["body.medium.light"]}
                        fontSize="sm"
                      >
                        Congratulations! You’re saving 25% on Taplio. Save
                        another 25% on Tweet Hunter by{" "}
                        <Link
                          isExternal
                          href="https://app.tweethunter.io/settings?tab=integrations"
                        >
                          following the same procedure in Tweet Hunter.
                        </Link>
                      </Text>
                    ) : (
                      <Text textStyle={textStyle["body.medium.light"]}>
                        Save{" "}
                        <Text
                          textStyle={textStyle["body.medium.light"]}
                          as={"span"}
                          fontWeight="600"
                          color="green.500"
                        >
                          25% on Taplio{" "}
                        </Text>{" "}
                        by connecting a Tweet Hunter account with an active subscription
                      </Text>
                    )}
                  </Box>
                ),
                home: "https://app.tweethunter.io",
                link: "https://app.tweethunter.io/settings?tab=integrations",
                ctaLabel: "Get my API key",
                image: "https://app.tweethunter.io/logo/logo_192.png",
              },
              {
                name: "lemlist",
                id: "keyLemlist",
                hasInputKey: true,
                desc: (
                  <Box>
                    <Text textStyle={textStyle["body.medium.light"]}>
                      Connect a lemlist account to push your leads there
                    </Text>
                  </Box>
                ),
                home: "https://lemlist.com",
                link: "https://app.lemlist.com/settings/integrations",
                ctaLabel: "Get my API key",
                // image: "/logo/lemlist_logo.png",
                // svg: <LemListIcon {...{width:80, height: 80}} />,
                image: lemlist
              },
              {
                name: "Zapier",
                desc: (
                  <Box>
                    <Text textStyle={textStyle["body.medium.light"]}>
                      Connect Taplio to any other app with Zapier. Triggers
                      available:
                    </Text>
                    <UnorderedList ml={10} mt="1">
                      <ListItem>
                        <b>Post published</b>, let you trigger actions after a
                        post has been published like telling your team
                      </ListItem>
                      <ListItem>
                        <b>Engagement received</b>, what about you push all
                        engagement received in your CRM?
                      </ListItem>
                    </UnorderedList>
                  </Box>
                ),
                home: "https://zapier.com",
                link: "https://zapier.com/apps/taplio/integrations",
                image: "/assets/resources/zapier.webp",
              },
            ].map((partner, index) => (
              <Partner
                partner={partner}
                index={index}
                key={"partner" + index}
              />
            ))}
          </Stack>
        </CustomSection>
      </Stack>
    </Box>
  );
}
